import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useAdminNotifications } from '../../../contexts/AdminNotificationContext';

const TYPE_STYLES = {
  critical: { dot: 'bg-red-500', icon: 'text-red-400', iconBg: 'bg-red-500/15' },
  warning: { dot: 'bg-orange-500', icon: 'text-orange-400', iconBg: 'bg-orange-500/15' },
  'warning-yellow': { dot: 'bg-yellow-500', icon: 'text-yellow-400', iconBg: 'bg-yellow-500/15' },
};

const formatRelativeTime = (date) => {
  if (!date) return '';
  const diff = Math.floor((Date.now() - date?.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const NotificationBell = ({ onNavigate }) => {
  const { bellNotifications, unreadCount, markAllRead } = useAdminNotifications();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkAllRead = () => {
    markAllRead();
  };

  const handleNotifClick = (notif) => {
    if (notif?.section && onNavigate) {
      onNavigate(notif?.section);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Icon name="Bell" size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-11 w-80 bg-card border border-border rounded-xl shadow-2xl z-[150] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={15} color="var(--color-primary)" />
              <span className="text-sm font-semibold text-foreground">Critical Alerts</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {bellNotifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <Icon name="BellOff" size={24} color="var(--color-muted-foreground)" />
                <p className="text-sm text-muted-foreground">No critical alerts</p>
              </div>
            ) : (
              bellNotifications?.map((notif) => {
                const style = TYPE_STYLES?.[notif?.type] || TYPE_STYLES?.critical;
                return (
                  <button
                    key={notif?.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left border-b border-border/50 last:border-0 ${
                      !notif?.read ? 'bg-primary/5' : ''
                    }`}
                  >
                    {/* Unread dot */}
                    <div className="flex-shrink-0 mt-1.5">
                      {!notif?.read ? (
                        <div className={`w-2 h-2 rounded-full ${style?.dot}`} />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-transparent" />
                      )}
                    </div>
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${style?.iconBg} flex items-center justify-center`}>
                      <Icon name={notif?.icon || 'AlertTriangle'} size={15} className={style?.icon} />
                    </div>
                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold ${notif?.read ? 'text-foreground/70' : 'text-foreground'} leading-tight`}>
                        {notif?.title}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                        {notif?.message}
                      </p>
                      <p className="text-[10px] text-muted-foreground/50 mt-1">
                        {formatRelativeTime(notif?.timestamp)}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {bellNotifications?.length > 0 && (
            <div className="px-4 py-2.5 border-t border-border bg-muted/30">
              <p className="text-[10px] text-muted-foreground text-center">
                Showing last {bellNotifications?.length} critical event{bellNotifications?.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
